import DashboardLayout from "@/components/DashboardLayout";
import { useState, useEffect } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Check, AlertCircle, Bot, Plus, X } from "lucide-react";
import ApiKeyManagement from "@/components/ApiKeyManagement";
import ApiUsageStats from "@/components/ApiUsageStats";

// 靜態選項，可依需求擴充
const COUNTRIES = [
  { value: "tw", label: "台灣" },
  { value: "us", label: "美國" },
  { value: "jp", label: "日本" },
  { value: "kr", label: "韓國" },
];

const LOCALES = [
  { value: "zh-TW", label: "中文 (繁體)" },
  { value: "en-US", label: "English (US)" },
  { value: "ja-JP", label: "日本語" },
  { value: "ko-KR", label: "한국어" },
];

type BrandSetting = {
  id: string;
  name: string;
  aliases: string[];
  domains: string[];
  primary?: boolean;
};

const DEFAULT_BRANDS: BrandSetting[] = [
  {
    id: "ymswood",
    name: "原木香",
    aliases: ["原木香", "YMSWOOD", "ymswood"],
    domains: ["www.ymswood.com.tw", "ymswood.com.tw"],
    primary: true,
  },
];

const uniqueValues = (values: string[]) =>
  Array.from(new Set(values.map(value => value.trim()).filter(Boolean)));

const normalizeDomain = (value: string) =>
  value
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/\/.*$/, "")
    .replace(/\.$/, "")
    .toLowerCase();

const toWebsiteUrl = (domain?: string) => {
  const normalized = normalizeDomain(domain || "");
  return normalized ? `https://${normalized}` : "";
};

const parseBrandsSetting = (
  raw: string | null | undefined,
  fallbackBrand?: string | null
): BrandSetting[] => {
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const brands = parsed
          .map((item, index) => ({
            id: typeof item?.id === "string" && item.id.trim() ? item.id.trim() : `brand-${index + 1}`,
            name: typeof item?.name === "string" ? item.name.trim() : "",
            aliases: Array.isArray(item?.aliases) ? uniqueValues(item.aliases.filter((alias: unknown) => typeof alias === "string")) : [],
            domains: Array.isArray(item?.domains)
              ? uniqueValues(item.domains.filter((domain: unknown) => typeof domain === "string").map(normalizeDomain))
              : [],
            primary: Boolean(item?.primary),
          }))
          .filter(brand => brand.name);

        if (brands.length > 0) {
          return brands.map((brand, index) => ({ ...brand, primary: index === 0 ? true : brand.primary }));
        }
      }
    } catch {
      // Fall back to legacy brand_name below.
    }
  }

  const legacyNames = uniqueValues((fallbackBrand || "").split("｜"));
  if (legacyNames.length > 0) {
    return legacyNames.map((name, index) => ({
      id: `brand-${index + 1}`,
      name,
      aliases: [name],
      domains: [],
      primary: index === 0,
    }));
  }

  return DEFAULT_BRANDS;
};

const sanitizeBrands = (brands: BrandSetting[]) =>
  brands
    .map((brand, index) => ({
      id: brand.id || `brand-${index + 1}`,
      name: brand.name.trim(),
      aliases: uniqueValues([brand.name, ...brand.aliases]),
      domains: uniqueValues(brand.domains.map(normalizeDomain)),
      primary: index === 0,
    }))
    .filter(brand => brand.name);

export default function Settings() {
  // Auth gating is handled by DashboardLayout; avoid hard redirects that can cause login loops.
  const { user } = useAuth();
  const [brands, setBrands] = useState<BrandSetting[]>(DEFAULT_BRANDS);
  const [aliasInputs, setAliasInputs] = useState<Record<string, string>>({});
  const [domainInputs, setDomainInputs] = useState<Record<string, string>>({});
  const [country, setCountry] = useState("");
  const [locale, setLocale] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "success" | "error">("idle");
  
  // LLM Citation 設定
  const [llmBrandName, setLlmBrandName] = useState("");
  const [llmBrandAliases, setLlmBrandAliases] = useState<string[]>([]);
  const [newAlias, setNewAlias] = useState("");
  const [llmCheckFrequency, setLlmCheckFrequency] = useState("weekly");
  const [llmEnableGemini, setLlmEnableGemini] = useState(true);
  const [llmEnableChatgpt, setLlmEnableChatgpt] = useState(false);
  const [llmDailyCheckLimit, setLlmDailyCheckLimit] = useState(50);
  const [llmSaveStatus, setLlmSaveStatus] = useState<"idle" | "success" | "error">("idle");

  // 讀取設定值
  const brandQuery = trpc.settings.get.useQuery({ key: "brand_name" }, { enabled: !!user });
  const brandsQuery = trpc.settings.get.useQuery({ key: "brands" }, { enabled: !!user });
  const countryQuery = trpc.settings.get.useQuery({ key: "country" }, { enabled: !!user });
  const localeQuery = trpc.settings.get.useQuery({ key: "locale" }, { enabled: !!user });

  const upsertMutation = trpc.settings.upsert.useMutation({
    onSuccess: () => {
      setSaveStatus("success");
      setTimeout(() => setSaveStatus("idle"), 3000);
    },
    onError: () => {
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 3000);
    },
  });
  
  // LLM Citation queries and mutations
  const { data: llmSettings } = trpc.llmCitation.getSettings.useQuery(undefined, { enabled: !!user });
  const updateLlmSettings = trpc.llmCitation.updateSettings.useMutation({
    onSuccess: () => {
      setLlmSaveStatus("success");
      setTimeout(() => setLlmSaveStatus("idle"), 3000);
    },
    onError: () => {
      setLlmSaveStatus("error");
      setTimeout(() => setLlmSaveStatus("idle"), 3000);
    },
  });

  useEffect(() => {
    if (brandsQuery.data?.value || brandQuery.data?.value) {
      setBrands(parseBrandsSetting(brandsQuery.data?.value, brandQuery.data?.value));
    }
    if (countryQuery.data?.value) setCountry(countryQuery.data.value);
    if (localeQuery.data?.value) setLocale(localeQuery.data.value);
  }, [brandsQuery.data, brandQuery.data, countryQuery.data, localeQuery.data]);
  
  useEffect(() => {
    if (llmSettings) {
      setLlmBrandName(llmSettings.brandName || "");
      setLlmBrandAliases(llmSettings.brandAliases || []);
      setLlmCheckFrequency(llmSettings.checkFrequency || "weekly");
      setLlmEnableGemini(llmSettings.enableGemini ?? true);
      setLlmEnableChatgpt(llmSettings.enableChatgpt ?? false);
      setLlmDailyCheckLimit(llmSettings.dailyCheckLimit || 50);
    }
  }, [llmSettings]);

  const handleSaveAll = async () => {
    setIsSaving(true);
    try {
      const normalizedBrands = sanitizeBrands(brands);
      const displayBrandName = normalizedBrands.map(item => item.name).join("｜");
      const allAliases = uniqueValues(normalizedBrands.flatMap(item => item.aliases));
      const allDomains = uniqueValues(normalizedBrands.flatMap(item => item.domains));
      const primaryWebsiteUrl = toWebsiteUrl(normalizedBrands[0]?.domains[0]);

      await upsertMutation.mutateAsync({ key: "brands", value: JSON.stringify(normalizedBrands), description: "多品牌監控設定" });
      await upsertMutation.mutateAsync({ key: "brand_name", value: displayBrandName, description: "brand_name 設定" });
      await upsertMutation.mutateAsync({ key: "llm_brand_name", value: displayBrandName, description: "LLM 檢查時使用的品牌名稱" });
      await upsertMutation.mutateAsync({ key: "llm_brand_aliases", value: JSON.stringify(allAliases), description: "LLM 檢查時的品牌別名" });
      await upsertMutation.mutateAsync({ key: "brand_domains", value: JSON.stringify(allDomains), description: "品牌網站網域清單" });
      if (primaryWebsiteUrl) {
        await upsertMutation.mutateAsync({ key: "website_url", value: primaryWebsiteUrl, description: "主要品牌網站" });
      }
      await upsertMutation.mutateAsync({ key: "country", value: country, description: "country 設定" });
      await upsertMutation.mutateAsync({ key: "locale", value: locale, description: "locale 設定" });

      setBrands(normalizedBrands);
      setLlmBrandName(displayBrandName);
      setLlmBrandAliases(allAliases);
    } finally {
      setIsSaving(false);
    }
  };

  const updateBrand = (id: string, update: Partial<BrandSetting>) => {
    setBrands(current => current.map(brand => brand.id === id ? { ...brand, ...update } : brand));
  };

  const addBrand = () => {
    const id = `brand-${Date.now()}`;
    setBrands(current => [
      ...current,
      { id, name: "", aliases: [], domains: [], primary: current.length === 0 },
    ]);
  };

  const removeBrand = (id: string) => {
    setBrands(current => {
      const next = current.filter(brand => brand.id !== id);
      return next.map((brand, index) => ({ ...brand, primary: index === 0 }));
    });
  };

  const addBrandAlias = (brandId: string) => {
    const value = aliasInputs[brandId]?.trim();
    if (!value) return;
    setBrands(current => current.map(brand => (
      brand.id === brandId
        ? { ...brand, aliases: uniqueValues([...brand.aliases, value]) }
        : brand
    )));
    setAliasInputs(current => ({ ...current, [brandId]: "" }));
  };

  const removeBrandAlias = (brandId: string, alias: string) => {
    setBrands(current => current.map(brand => (
      brand.id === brandId
        ? { ...brand, aliases: brand.aliases.filter(item => item !== alias) }
        : brand
    )));
  };

  const addBrandDomain = (brandId: string) => {
    const value = normalizeDomain(domainInputs[brandId] || "");
    if (!value) return;
    setBrands(current => current.map(brand => (
      brand.id === brandId
        ? { ...brand, domains: uniqueValues([...brand.domains, value]) }
        : brand
    )));
    setDomainInputs(current => ({ ...current, [brandId]: "" }));
  };

  const removeBrandDomain = (brandId: string, domain: string) => {
    setBrands(current => current.map(brand => (
      brand.id === brandId
        ? { ...brand, domains: brand.domains.filter(item => item !== domain) }
        : brand
    )));
  };
  
  const handleAddAlias = () => {
    if (newAlias.trim() && !llmBrandAliases.includes(newAlias.trim())) {
      setLlmBrandAliases([...llmBrandAliases, newAlias.trim()]);
      setNewAlias("");
    }
  };
  
  const handleRemoveAlias = (alias: string) => {
    setLlmBrandAliases(llmBrandAliases.filter(a => a !== alias));
  };
  
  const handleSaveLlmSettings = async () => {
    await updateLlmSettings.mutateAsync({
      brandName: llmBrandName,
      brandAliases: llmBrandAliases,
      checkFrequency: llmCheckFrequency as 'never' | 'daily' | 'weekly' | 'monthly',
      enableGemini: llmEnableGemini,
      enableChatgpt: llmEnableChatgpt,
      dailyCheckLimit: llmDailyCheckLimit
    });
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <DashboardLayout>
      <div className="container max-w-4xl py-8">
        <div className="space-y-6">
          {/* 返回首頁 */}
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold">設定</h1>
            <a href="/" className="text-primary hover:underline">
              ← 返回首頁
            </a>
          </div>
          <p className="text-muted-foreground mt-2">管理系統設定與 API 金鑰</p>

          {/* 基本資訊：品牌、國家、語系 */}
          <Card>
            <CardHeader>
              <CardTitle>基本資訊</CardTitle>
              <CardDescription>設定監控品牌、目標國家與語系</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <Label>監控品牌</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addBrand} disabled={isSaving}>
                    <Plus className="h-4 w-4 mr-1" />
                    新增品牌
                  </Button>
                </div>
                <div className="space-y-3">
                  {brands.map((item, index) => (
                    <div key={item.id} className="rounded-md border p-3 space-y-3">
                      <div className="flex items-center gap-2">
                        <Input
                          value={item.name}
                          onChange={e => updateBrand(item.id, { name: e.target.value })}
                          placeholder="品牌名稱"
                          disabled={isSaving}
                          className="flex-1"
                        />
                        {index === 0 && (
                          <Badge variant="secondary" className="h-8 px-3">主要</Badge>
                        )}
                        {brands.length > 1 && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeBrand(item.id)} disabled={isSaving}>
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm">別名</Label>
                        <div className="flex flex-wrap gap-2">
                          {item.aliases.map(alias => (
                            <Badge key={alias} variant="outline" className="h-8 gap-1 px-3">
                              {alias}
                              <button
                                type="button"
                                className="ml-1 rounded-sm hover:bg-muted"
                                onClick={() => removeBrandAlias(item.id, alias)}
                                disabled={isSaving}
                                aria-label={`移除 ${alias}`}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <Input
                            value={aliasInputs[item.id] || ""}
                            onChange={e => setAliasInputs(current => ({ ...current, [item.id]: e.target.value }))}
                            onKeyDown={e => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                addBrandAlias(item.id);
                              }
                            }}
                            placeholder="新增別名"
                            disabled={isSaving}
                          />
                          <Button type="button" variant="outline" onClick={() => addBrandAlias(item.id)} disabled={isSaving || !aliasInputs[item.id]?.trim()}>
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm">網站網域</Label>
                        <div className="flex flex-wrap gap-2">
                          {item.domains.map(domain => (
                            <Badge key={domain} variant="secondary" className="h-8 gap-1 px-3">
                              {domain}
                              <button
                                type="button"
                                className="ml-1 rounded-sm hover:bg-muted"
                                onClick={() => removeBrandDomain(item.id, domain)}
                                disabled={isSaving}
                                aria-label={`移除 ${domain}`}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <Input
                            value={domainInputs[item.id] || ""}
                            onChange={e => setDomainInputs(current => ({ ...current, [item.id]: e.target.value }))}
                            onKeyDown={e => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                addBrandDomain(item.id);
                              }
                            }}
                            placeholder="新增網域，例如 www.ymswood.com.tw"
                            disabled={isSaving}
                          />
                          <Button type="button" variant="outline" onClick={() => addBrandDomain(item.id)} disabled={isSaving || !domainInputs[item.id]?.trim()}>
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="country-select">國家</Label>
                <select
                  id="country-select"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={country}
                  onChange={e => setCountry(e.target.value)}
                  disabled={isSaving}
                >
                  <option value="">請選擇國家</option>
                  {COUNTRIES.map(c => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="locale-select">語系</Label>
                <select
                  id="locale-select"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={locale}
                  onChange={e => setLocale(e.target.value)}
                  disabled={isSaving}
                >
                  <option value="">請選擇語系</option>
                  {LOCALES.map(l => (
                    <option key={l.value} value={l.value}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end">
                <Button onClick={handleSaveAll} disabled={isSaving || sanitizeBrands(brands).length === 0 || !country || !locale}>
                  {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  儲存設定
                </Button>
              </div>
              {saveStatus === "success" && (
                <Alert className="bg-green-50 border-green-200">
                  <Check className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">設定已儲存</AlertDescription>
                </Alert>
              )}
              {saveStatus === "error" && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>儲存失敗，請稍後再試</AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* API 金鑰管理（多組 SerpAPI 金鑰） */}
          <Card>
            <CardHeader>
              <CardTitle>API 金鑰管理</CardTitle>
              <CardDescription>管理多組 SerpAPI 金鑰，支援自動輪替</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ApiKeyManagement />
            </CardContent>
          </Card>

          {/* API 使用量統計 */}
          <Card>
            <CardHeader>
              <CardTitle>API 使用量</CardTitle>
              <CardDescription>追蹤 API Token 消耗與費用</CardDescription>
            </CardHeader>
            <CardContent>
              <ApiUsageStats />
            </CardContent>
          </Card>

          {/* LLM 引用監控設定 */}
          <Card className="border-purple-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5 text-purple-600" />
                LLM 引用監控設定
              </CardTitle>
              <CardDescription>設定品牌名稱與 LLM 檢查參數</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* 品牌名稱 */}
              <div className="space-y-2">
                <Label htmlFor="llm-brand-name">品牌名稱 *</Label>
                <Input
                  id="llm-brand-name"
                  value={llmBrandName}
                  onChange={e => setLlmBrandName(e.target.value)}
                  placeholder="例如：原木香"
                  disabled={updateLlmSettings.isPending}
                />
                <p className="text-xs text-muted-foreground">
                  用於在 LLM 回應中檢測品牌提及
                </p>
              </div>

              {/* 品牌別名 */}
              <div className="space-y-2">
                <Label>品牌別名（可選）</Label>
                <div className="space-y-2">
                  {llmBrandAliases.map((alias, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input value={alias} disabled className="flex-1" />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveAlias(alias)}
                        disabled={updateLlmSettings.isPending}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex items-center gap-2">
                    <Input
                      value={newAlias}
                      onChange={e => setNewAlias(e.target.value)}
                      placeholder="輸入別名，例如：悦庭"
                      onKeyPress={e => e.key === 'Enter' && handleAddAlias()}
                      disabled={updateLlmSettings.isPending}
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAddAlias}
                      disabled={!newAlias.trim() || updateLlmSettings.isPending}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      新增
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  設定品牌的其他稱呼方式，提高檢測準確度
                </p>
              </div>

              {/* 檢查頻率 */}
              <div className="space-y-2">
                <Label htmlFor="llm-check-frequency">自動檢查頻率</Label>
                <select
                  id="llm-check-frequency"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={llmCheckFrequency}
                  onChange={e => setLlmCheckFrequency(e.target.value)}
                  disabled={updateLlmSettings.isPending}
                >
                  <option value="never">從不自動檢查</option>
                  <option value="daily">每日</option>
                  <option value="weekly">每週</option>
                  <option value="monthly">每月</option>
                </select>
              </div>

              {/* 啟用的 LLM */}
              <div className="space-y-3">
                <Label>啟用的 LLM 平台</Label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={llmEnableGemini}
                      onChange={e => setLlmEnableGemini(e.target.checked)}
                      disabled={updateLlmSettings.isPending}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">啟用 Gemini 檢查</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={llmEnableChatgpt}
                      onChange={e => setLlmEnableChatgpt(e.target.checked)}
                      disabled={updateLlmSettings.isPending}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">啟用 ChatGPT 檢查（需要 API Key）</span>
                  </label>
                </div>
              </div>

              {/* 每日檢查上限 */}
              <div className="space-y-2">
                <Label htmlFor="llm-daily-limit">每日檢查上限</Label>
                <Input
                  id="llm-daily-limit"
                  type="number"
                  min="1"
                  max="200"
                  value={llmDailyCheckLimit}
                  onChange={e => setLlmDailyCheckLimit(parseInt(e.target.value) || 50)}
                  disabled={updateLlmSettings.isPending}
                />
                <p className="text-xs text-muted-foreground">
                  避免 API 費用過高，建議設定為 50
                </p>
              </div>

              {/* 儲存按鈕 */}
              <div className="flex justify-end">
                <Button
                  onClick={handleSaveLlmSettings}
                  disabled={updateLlmSettings.isPending || !llmBrandName}
                >
                  {updateLlmSettings.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  儲存 LLM 設定
                </Button>
              </div>

              {/* 狀態提示 */}
              {llmSaveStatus === "success" && (
                <Alert className="bg-green-50 border-green-200">
                  <Check className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">LLM 設定已儲存</AlertDescription>
                </Alert>
              )}
              {llmSaveStatus === "error" && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>儲存失敗，請稍後再試</AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
